import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

import { APIStatus } from "../../../../api/APIStatus.type";
import { overviewIcon, tacticIcon } from "../../../../designSystemIcons";
import {
  LeftPanelMode,
  Squad,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { Tactic } from "../../../../redux/tactics/tacticsSlice";
import { OnBoardingNumber } from "../../OnBoarding/OnBoardingNumber";
import { ChemistryOverviewMode } from "./ChemistryOverviewMode/ChemistryOverviewMode";
import { TacticMode } from "./TacticMode/TacticMode";

type Props = {
  squad: Squad;
  tempSquad: Squad;
  desktopMode: boolean;
  formations: Formations;
  leftPanelMode: LeftPanelMode;
  onBoardingStep?: number;
  isShowingOnBoarding?: boolean;
  chosenTactic: Tactic;
  handleSwitchLeftPanelMode?: (type: "overview" | "tactics") => void;
  handleCopy: () => void;
  handleChangeFormation?: (formationId: string) => void;
  handleChangeBuildUpStyle?: (buildUpStyle: string) => void;
  handleChangeDefensiveApproach?: (defensiveApproach: string) => void;
  handleChangeLineHeight?: (lineHeight: number) => void;
  handleGetTacticFromCode?: (code: string) => void;
  statusInsertTacticCode?: APIStatus;
  scrollPastTopBar: boolean;
};

type LeftPanelStickerProps = {
  type: "overview" | "tactics";
  onClick: () => void;
  isActive?: boolean;
};

const LeftPanelSticker = ({
  type,
  onClick,
  isActive,
}: LeftPanelStickerProps) => {
  const { t } = useTranslation("common");
  return (
    <button
      className={cn(
        "p-2 rounded-r-lg w-fit border-b border-r border-t",
        // Quiet selected state, as SegmentedControl's secondary pill
        isActive
          ? "bg-control border-edge-strongest text-foreground"
          : "hover:pl-4 bg-surface border-edge-strong text-foreground-muted hover:bg-surface-raised hover:text-foreground",
        "transition-all duration-200 ease-in-out",
      )}
      onClick={onClick}
    >
      {/* Fixed width so both stickers match */}
      <span className="flex flex-col items-center gap-y-1 w-13 [&>svg]:size-6">
        {type === "tactics" ? tacticIcon : overviewIcon}
        <span className="text-xxs font-semibold leading-none">
          {t(type === "tactics" ? "tactic" : "chemistry")}
        </span>
      </span>
    </button>
  );
};

export const LeftPanel = ({
  squad,
  tempSquad,
  desktopMode,
  formations,
  leftPanelMode,
  onBoardingStep,
  isShowingOnBoarding,
  chosenTactic,
  handleSwitchLeftPanelMode,
  handleCopy,
  handleChangeFormation,
  handleChangeBuildUpStyle,
  handleChangeDefensiveApproach,
  handleChangeLineHeight,
  handleGetTacticFromCode,
  statusInsertTacticCode,
  scrollPastTopBar,
}: Props) => {
  const topToPage = useSelector(topToPageSelector);
  return (
    <>
      <div
        className={cn(
          "mt-30 z-30 touch-device:flex flex-col gap-y-2 hidden w-fit",
          scrollPastTopBar && desktopMode ? "fixed" : "absolute",
          scrollPastTopBar && desktopMode
            ? "desktop:left-[calc(max(0px,calc((100%-2000px)/2))+260px)] sb-v2-lg:left-[calc(max(0px,calc((100%-2000px)/2))+352px)]"
            : "desktop:left-[260px] sb-v2-lg:left-[352px]",
          scrollPastTopBar && desktopMode && topToPage.tailwindString,
        )}
      >
        <LeftPanelSticker
          type={"overview"}
          isActive={leftPanelMode !== "tactics"}
          onClick={() => handleSwitchLeftPanelMode("overview")}
        />
        <LeftPanelSticker
          type={"tactics"}
          isActive={leftPanelMode === "tactics"}
          onClick={() => handleSwitchLeftPanelMode("tactics")}
        />
      </div>
      <section
        className={cn(
          "w-full desktop:w-[260px] sb-v2-lg:w-[352px] h-full desktop:bottom-0 min-h-screen mobile:min-h-50 bg-side-panel shadow-left-panel px-6 mobile:px-0 pb-6 mobile:pb-0",
          !isShowingOnBoarding && "z-21",
          scrollPastTopBar && desktopMode
            ? "fixed left-[max(0px,calc((100%-2000px)/2))]"
            : "absolute overflow-y-auto left-0",
          leftPanelMode === "tactics" && "right-0",
          scrollPastTopBar && desktopMode && topToPage.tailwindString,
        )}
      >
        {onBoardingStep === 1 && (
          <div className="absolute top-[460px] -right-4 sb-v2-lg:right-2 z-21">
            <OnBoardingNumber number={1} />
          </div>
        )}
        {leftPanelMode === "chem-overview" ? (
          <ChemistryOverviewMode squad={squad} tempSquad={tempSquad} />
        ) : leftPanelMode === "tactics" ? (
          <TacticMode
            formations={formations}
            chosenTactic={chosenTactic}
            mode="squad-builder"
            handleCopy={handleCopy}
            handleChangeBuildUpStyle={handleChangeBuildUpStyle}
            handleChangeDefensiveApproach={handleChangeDefensiveApproach}
            handleChangeFormation={handleChangeFormation}
            handleChangeLineHeight={handleChangeLineHeight}
            handleGetTacticFromCode={handleGetTacticFromCode}
            statusInsertTacticCode={statusInsertTacticCode}
            desktopMode={desktopMode}
          />
        ) : null}
      </section>
    </>
  );
};
